import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AccountsListView from '../components/assignments/AccountsListView';
import AccountDetailView from '../components/assignments/AccountDetailView';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import AssignUsersGroupsModal from '../components/assignments/AssignUsersGroupsModal';
import ChangePermissionSetsModal from '../components/assignments/ChangePermissionSetsModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import AccountOwnersModal from '../components/AccountOwnersModal';
import apiService from '../services/api';

const PermissionSetAssignments = ({
  users,
  groups = [],
  permissionSets,
  permissionSetAssignments,
  fetchAllUsers,
  fetchAllGroups,
  fetchAllPermissionSets,
  fetchPermissionSetAssignments,
  getPermissionSetAssignment,
  createPermissionSetAssignment,
  deletePermissionSetAssignment,
}) => {
  const navigate = useNavigate();
  const dataFetchedRef = useRef(false);
  const isReadOnly = useReadOnlyMode();

  // View state: null = accounts list, object = account detail
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Display value
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(''); // Value sent to API
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

  // AWS Accounts pagination state
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [accountsPagination, setAccountsPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const searchTimeoutRef = useRef(null);

  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showChangePermissionsModal, setShowChangePermissionsModal] = useState(false);
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState([]);
  const [selectedAccountsForAssignment, setSelectedAccountsForAssignment] = useState([]);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    assignmentIds: [],
    isLoading: false,
  });

  // Account owners modal state
  const [showOwnersModal, setShowOwnersModal] = useState(false);
  const [accountForOwners, setAccountForOwners] = useState(null);

  // Track if this is the initial load
  const isInitialLoadRef = useRef(true);

  // Fetch AWS accounts with pagination
  const fetchAwsAccountsPaginated = useCallback(async () => {
    // Only show full loading state on initial load
    if (isInitialLoadRef.current) {
      setIsLoadingAccounts(true);
    }
    try {
      const response = await apiService.getAwsAccountsFromDB({
        page: accountsPagination.page + 1, // API uses 1-indexed pages
        pageSize: accountsPagination.pageSize,
        search: debouncedSearchQuery || undefined,
      });
      if (response.success && response.data) {
        const accounts = response.data.accounts || [];
        setAwsAccounts(accounts);
        setAccountsPagination(prev => ({
          ...prev,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0,
        }));

      }
    } catch (error) {
      console.error('Error fetching AWS accounts:', error);
    } finally {
      setIsLoadingAccounts(false);
      isInitialLoadRef.current = false;
    }
  }, [accountsPagination.page, accountsPagination.pageSize, debouncedSearchQuery]);

  // Handle search with debounce
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value); // Update display value immediately

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(value); // Update API search value after debounce
      setAccountsPagination(prev => ({ ...prev, page: 0 })); // Reset to first page on search
    }, 300);
  }, []);

  // Fetch initial data
  useEffect(() => {
    if (!dataFetchedRef.current) {
      const fetchData = async () => {
        try {
          const fetchPromises = [];

          if (users.length === 0) fetchPromises.push(fetchAllUsers());
          if (groups.length === 0 && fetchAllGroups) fetchPromises.push(fetchAllGroups());
          if (permissionSets.length === 0) fetchPromises.push(fetchAllPermissionSets());

          fetchPromises.push(fetchPermissionSetAssignments());

          await Promise.allSettled(fetchPromises);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };

      fetchData();
      dataFetchedRef.current = true;
    }
  }, [
    fetchAllUsers,
    fetchAllGroups,
    fetchAllPermissionSets,
    fetchPermissionSetAssignments,
    users.length,
    groups.length,
    permissionSets.length,
  ]);

  // Fetch AWS accounts when pagination or search changes
  useEffect(() => {
    fetchAwsAccountsPaginated();
  }, [fetchAwsAccountsPaginated]);

  // Group assignments by account and compute permission sets for each account
  const accountsWithAssignments = useMemo(() => {
    const accountMap = new Map();

    // Initialize with all AWS accounts
    awsAccounts.forEach((account) => {
      const accountId = account.accountId || account.account_id;
      accountMap.set(accountId, {
        ...account,
        accountId,
        accountName: account.accountName || account.name,
        email: account.email || '',
        assignments: [],
        permissionSets: [],
      });
    });

    // Add assignments to accounts
    permissionSetAssignments.forEach((assignment) => {
      const accountData = accountMap.get(assignment.accountId);
      if (accountData) {
        accountData.assignments.push(assignment);
        // Add unique permission set names
        if (!accountData.permissionSets.includes(assignment.permissionSetName)) {
          accountData.permissionSets.push(assignment.permissionSetName);
        }
      }
    });

    return Array.from(accountMap.values());
  }, [awsAccounts, permissionSetAssignments]);

  // Get assignments for the selected account
  const selectedAccountAssignments = useMemo(() => {
    if (!selectedAccount) return [];
    const accountId = selectedAccount.accountId || selectedAccount.account_id;
    return permissionSetAssignments.filter((a) => a.accountId === accountId);
  }, [selectedAccount, permissionSetAssignments]);

  // Handlers
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchPermissionSetAssignments();
      await fetchAwsAccountsPaginated();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAccountClick = async (account) => {
    setSelectedAccount(account);
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setSelectedAssignmentIds([]);
    setIsLoadingAssignments(true);
    try {
      await fetchPermissionSetAssignments();
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  const handleBackToAccounts = () => {
    setSelectedAccount(null);
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setSelectedAssignmentIds([]);
  };

  const handleAssignUsersGroups = () => {
    setSelectedAccountsForAssignment([]);
    setShowAssignModal(true);
  };

  const handleAssignToAccounts = (accounts) => {
    setSelectedAccountsForAssignment(accounts);
    setShowAssignModal(true);
  };

  const handleChangePermissionSets = (assignmentIds) => {
    setSelectedAssignmentIds(assignmentIds);
    setShowChangePermissionsModal(true);
  };

  const handleRemoveAccess = (assignmentIds) => {
    if (!assignmentIds || assignmentIds.length === 0) return;

    const confirmMessage =
      assignmentIds.length === 1
        ? 'Are you sure you want to remove this access? This action cannot be undone.'
        : `Are you sure you want to remove ${assignmentIds.length} assignments? This action cannot be undone.`;

    setConfirmDialog({
      open: true,
      title: 'Remove Access',
      message: confirmMessage,
      assignmentIds,
      isLoading: false,
    });
  };

  const handleConfirmRemoveAccess = async () => {
    const { assignmentIds } = confirmDialog;

    setConfirmDialog((prev) => ({ ...prev, isLoading: true }));

    try {
      let successCount = 0;
      for (const id of assignmentIds) {
        // Pass true to skip the useApi confirmation since we already confirmed
        const success = await deletePermissionSetAssignment(id, true);
        if (success) successCount++;
      }

      if (successCount > 0) {
        toast.success(`Successfully removed ${successCount} assignment(s)`);
        await fetchPermissionSetAssignments();
        setSelectedAssignmentIds([]);
      }
    } catch (error) {
      console.error('Error removing access:', error);
      toast.error('Failed to remove access');
    } finally {
      setConfirmDialog({
        open: false,
        title: '',
        message: '',
        assignmentIds: [],
        isLoading: false,
      });
    }
  };

  const handleCancelRemoveAccess = () => {
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      assignmentIds: [],
      isLoading: false,
    });
  };

  const handleCreateAssignment = async (assignmentData) => {
    try {
      const success = await createPermissionSetAssignment(assignmentData);
      if (success) {
        await fetchPermissionSetAssignments();
      }
      return success;
    } catch (error) {
      console.error('Error creating assignment:', error);
      return false;
    }
  };

  const handleDeleteAssignment = async (id, skipConfirm = true) => {
    try {
      // Skip confirmation by default since ChangePermissionSetsModal handles its own confirmation
      const success = await deletePermissionSetAssignment(id, skipConfirm);
      if (success) {
        await fetchPermissionSetAssignments();
      }
      return success;
    } catch (error) {
      console.error('Error deleting assignment:', error);
      return false;
    }
  };

  // Navigation handlers
  const handleNavigateToUser = useCallback(
    (username) => {
      navigate('/users', { state: { selectUser: username } });
    },
    [navigate]
  );

  const handleNavigateToGroup = useCallback(
    (groupName) => {
      navigate('/groups', { state: { selectGroup: groupName } });
    },
    [navigate]
  );

  const handleNavigateToPermissionSet = useCallback(
    (id) => {
      navigate('/permission-sets', { state: { selectPermissionSet: id } });
    },
    [navigate]
  );

  // Handle edit owners
  const handleEditOwners = (account) => {
    setAccountForOwners(account);
    setShowOwnersModal(true);
  };

  const handleOwnersModalClose = () => {
    setShowOwnersModal(false);
    setAccountForOwners(null);
  };

  const handleOwnersSuccess = async () => {
    // Refresh accounts to get updated owner emails
    await fetchAwsAccountsPaginated();
    // If we're viewing an account detail, update the selected account
    if (selectedAccount && accountForOwners) {
      const accountId = selectedAccount.accountId || selectedAccount.account_id;
      const updatedAccount = awsAccounts.find(
        (a) => (a.accountId || a.account_id) === accountId
      );
      if (updatedAccount) {
        setSelectedAccount({ ...selectedAccount, ...updatedAccount });
      }
    }
  };

  // Render accounts list view
  if (!selectedAccount) {
    return (
      <>
        <AccountsListView
          accounts={accountsWithAssignments}
          onAccountClick={handleAccountClick}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          isLoading={isLoadingAccounts}
          onAssignToAccounts={handleAssignToAccounts}
          pagination={accountsPagination}
          onPageChange={(newPage) => setAccountsPagination(prev => ({ ...prev, page: newPage }))}
          onPageSizeChange={(newPageSize) => setAccountsPagination(prev => ({ ...prev, pageSize: newPageSize, page: 0 }))}
          isReadOnly={isReadOnly}
        />

        {/* Assign Users/Groups Modal for multiple accounts */}
        {showAssignModal && selectedAccountsForAssignment.length > 0 && (
          <AssignUsersGroupsModal
            accounts={selectedAccountsForAssignment}
            users={users}
            groups={groups}
            permissionSets={permissionSets}
            existingAssignments={[]}
            onSubmit={handleCreateAssignment}
            onClose={() => {
              setShowAssignModal(false);
              setSelectedAccountsForAssignment([]);
            }}
          />
        )}
      </>
    );
  }

  // Render account detail view
  return (
    <>
      <AccountDetailView
        account={selectedAccount}
        assignments={selectedAccountAssignments}
        users={users}
        groups={groups}
        permissionSets={permissionSets}
        onBack={handleBackToAccounts}
        onAssignUsersGroups={handleAssignUsersGroups}
        onChangePermissionSets={handleChangePermissionSets}
        onRemoveAccess={handleRemoveAccess}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        isLoadingAssignments={isLoadingAssignments}
        onNavigateToUser={handleNavigateToUser}
        onNavigateToGroup={handleNavigateToGroup}
        onNavigateToPermissionSet={handleNavigateToPermissionSet}
        onEditOwners={handleEditOwners}
        isReadOnly={isReadOnly}
      />

      {/* Assign Users/Groups Modal */}
      {showAssignModal && (
        <AssignUsersGroupsModal
          account={selectedAccount}
          users={users}
          groups={groups}
          permissionSets={permissionSets}
          existingAssignments={selectedAccountAssignments}
          onSubmit={handleCreateAssignment}
          onClose={() => setShowAssignModal(false)}
        />
      )}

      {/* Change Permission Sets Modal */}
      {showChangePermissionsModal && selectedAssignmentIds.length > 0 && (
        <ChangePermissionSetsModal
          account={selectedAccount}
          selectedAssignmentIds={selectedAssignmentIds}
          assignments={selectedAccountAssignments}
          permissionSets={permissionSets}
          onAddPermissionSet={handleCreateAssignment}
          onRemoveAssignment={handleDeleteAssignment}
          onClose={() => {
            setShowChangePermissionsModal(false);
            setSelectedAssignmentIds([]);
          }}
        />
      )}

      {/* Confirm Remove Access Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        isLoading={confirmDialog.isLoading}
        onConfirm={handleConfirmRemoveAccess}
        onCancel={handleCancelRemoveAccess}
      />

      {/* Account Owners Modal */}
      <AccountOwnersModal
        open={showOwnersModal}
        account={accountForOwners}
        onClose={handleOwnersModalClose}
        onSuccess={handleOwnersSuccess}
        users={users}
      />
    </>
  );
};

export default PermissionSetAssignments;
